import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Prompt } from "common/models/types";
import Text from "components/atoms/Text";
import Switch from "components/atoms/Switch";

interface PromptItemProps {
  prompt: Prompt;
  isSelected: boolean;
  onClick: () => void;
  onToggleActive?: (promptId: number, isActive: boolean) => void;
}

// 아이템 컨테이너 스타일
const Container = styled.div<{ isSelected: boolean; isActive: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Skyblue")?.[50] : "white"};
  border-radius: 6px;
  cursor: pointer;
  margin: 4px 0;
  transition: background-color 0.15s ease;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? COLOR_SYSTEM.get("Skyblue")?.[200] : "transparent")};

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Skyblue")?.[50] : COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;


// 정보 컨테이너
const InfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// 상단 정보 행 (버전과 날짜)
const InfoTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

// 하단 정보 행 (설명)
const InfoBottomRow = styled.div`
  display: flex;
  align-items: center;
`;

// 버전 메인 텍스트
const VersionText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: inherit;
`;

// 날짜 텍스트
const DateText = styled.div`
  font-size: 11px;
  color: inherit;
  opacity: 0.7;
`;

// 활성화 토글 스위치 컨테이너
const SwitchContainer = styled.div`
  margin-left: 12px;
  display: flex;
  align-items: center;
`;

// 날짜 포맷 함수
const formatDate = (date?: Date): string => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

const PromptItem: React.FC<PromptItemProps> = ({ prompt, isSelected, onClick, onToggleActive }) => {
  const isActive = prompt.isActive || false;
  const description = prompt.description || "설명 없음";
  const version = prompt.version || "v1.0.0";
  const createdDate = formatDate(prompt.createdAt);

  // 선택된 상태에 따라 색상 설정
  const textColor = isSelected
    ? COLOR_SYSTEM.get("Skyblue")?.[700]
    : COLOR_SYSTEM.get("Gray")?.[800];
  const metaTextColor = isSelected
    ? COLOR_SYSTEM.get("Skyblue")?.[600]
    : COLOR_SYSTEM.get("Gray")?.[500];

  // 토글 클릭 처리 - 이벤트 전파 방지
  const handleToggleClick = (e?: React.MouseEvent) => {
    // 이벤트 객체가 있을 경우에만 stopPropagation 호출
    if (e) {
      e.stopPropagation(); // 상위 컨테이너 클릭 이벤트 전파 방지
    }
    if (onToggleActive && prompt.id) {
      onToggleActive(prompt.id, !isActive);
    }
  };

  return (
    <Container isSelected={isSelected} isActive={isActive} onClick={onClick}>
      <InfoContainer>
        <InfoTopRow>
          <VersionText style={{ color: textColor }}>
            {version} / ID#{prompt.id}
          </VersionText>
          <DateText style={{ color: metaTextColor }}>{createdDate || "날짜 없음"}</DateText>
        </InfoTopRow>
        <InfoBottomRow>
          <Text
            textType="Caption"
            style={{
              color: metaTextColor,
              fontSize: "12px",
              lineHeight: "1.3",
            }}
          >
            {description}
          </Text>
        </InfoBottomRow>
      </InfoContainer>

      {/* 활성화 토글 스위치 */}
      {onToggleActive && prompt.id && (
        <SwitchContainer onClick={handleToggleClick}>
          <Switch
            size="Small"
            isActive={isActive}
            onClick={() => {
              /* 빈 함수: 이벤트 중복 호출 방지 */
            }}
          />
        </SwitchContainer>
      )}
    </Container>
  );
};

// 타입별 페이지 로드 시 다른 타입 항목까지 리렌더되지 않도록 메모이제이션.
// (부모는 onSelectPrompt/onToggleActive를 useCallback으로 안정화한다)
export default React.memo(PromptItem);
